import React, { Fragment, useContext, useCallback, useState } from 'react';
import {
  bool, shape, string, number
} from 'prop-types';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Row, Col } from '@thd-olt-component-react/grid';
import {
  MajorApplianceFulfillment,
  isShowBoth,
  isTwoTile,
  isTwoTileOff
} from '@thd-olt-component-react/fulfillment';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import classNames from 'classnames';
import {
  alias,
  params,
  client,
  extend,
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  shape as shapeType,
  string as stringType,
  number as numberType,
  bool as boolType,
  arrayOf as arrayOfType,
} from '@thd-nucleus/data-sources/react/dataModel/prop-types';
import { BuyboxError } from './subcomponents/BuyboxError';
import { BuyboxLoader } from './subcomponents/BuyboxLoader';
import * as helper from '../BuyboxHelper';
import { FULFILLMENT_METHOD_APPLIANCE
  , ATC_TEXT, BOGO_ATC_TEXT } from '../constants';
import { isMobile } from '../util';
import { useBogoInfo } from './hooks/useBogoInfo';

import './buybox.scss';
import { BuyboxAddToList } from './subcomponents/BuyboxAddToList';

/**
 * Buybox container
 */
const MajorApplianceBuyBox = (props) => {
  const {
    channel,
    isServer
  } = useContext(ExperienceContext);
  const {
    stash,
    showCards,
    showFavorites
  } = props;

  const bogoInfo = useBogoInfo();

  let { itemId } = props;

  const covidAlert = useConfigService('fs-prop:buy-area__buybox--covidAlert');
  const bopisMinimumThreshold = useConfigService('fs-prop:buy-area__buybox--bopisMinimumThreshold');
  const bopisMinimumThresholdStores = useConfigService('fs-prop:buy-area__buybox--bopisMinimumThresholdStores');
  const isTwoTileEnabledGlobal = useConfigService('isTwoTileEnabled');
  const isTwoTileEnabled = useConfigService('fs-prop:buy-area__buybox--isTwoTileEnabled');

  const isTwoTileEnabledFS = (
    (isTwoTileEnabledGlobal && isTwoTileEnabled)
    || ((isTwoTile() || isShowBoth()) && !isTwoTileOff())
  );
  const { storeId, storeZip } = useStore();
  const state = {
    fulfillmentMethod: FULFILLMENT_METHOD_APPLIANCE,
    isShowATC: false,
    zipCode: '',
    disableATC: false,
    quantity: 1
  };
  const getItemIdFromConfigurator = (items) => {
    if (items) {
      return items.find((item) => item.isAnchor).itemId.toString();
    }
    return '';
  };
  const [buyboxState, setBuyboxState] = useState(state);
  const { items } = useLifeCycleEventBus('configurator.configurator_available_items');
  let configurableApplianceItemIds;
  if (items) {
    itemId = getItemIdFromConfigurator(items) || itemId;
    configurableApplianceItemIds = items.map((item) => item.itemId.toString());
  }
  const atcStash = helper.useATCLocationDDOStash({ stash, itemId });

  const productOptions = {
    ssr: false,
    variables: {
      itemId,
      storeId,
      zipCode: storeZip
    }
  };

  const { data, loading, error } = useDataModel('clientOnlyProduct', productOptions);

  const {
    fulfillment = {}
  } = data?.product || {};

  const onFulfillmentChange = ({
    method,
    isShowATC,
    zipCode,
    disableATC,
    primaryStoreNum
  }) => {
    setBuyboxState((prevState) => {

      return {
        ...prevState,
        fulfillmentMethod: method,
        isShowATC,
        zipCode,
        disableATC,
        primaryStoreNum
      };
    });
  };

  // call back on add to cart click
  const onATCClick = useCallback(() => {
    if (window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('@@ADD-TO-CART/LOCATION', atcStash);
    }
  }, []);

  const {
    isShowATC,
    quantity,
    zipCode,
    fulfillmentMethod,
    disableATC,
    primaryStoreNum
  } = buyboxState;

  if ((error && !data)) {
    return (
      <BuyboxError />
    );
  }

  if (loading || isServer) {
    return (
      <BuyboxLoader tilesCount={2} />
    );
  }

  const wrapperClasses = classNames('buybox-wrapper', { 'buybox-wrapper--card-background': showCards });
  const desktopPadding = isMobile(channel) ? '' : 'desktop-card-padding';
  const mobileZone = classNames('buybox__card',
    { buybox__card_mobile_zone: showCards, 'buybox-top': !showCards });
  const cardClasses = (showCards && isMobile(channel)) ? 'buybox__card__centered' : 'buybox__card';
  const buttonText = bogoInfo?.bogoSelectedProduct?.itemId ? BOGO_ATC_TEXT : ATC_TEXT;

  const WrapperTag = Row;

  if (!data) {
    return null;
  }

  return (
    <WrapperTag>
      <Col fallback="12" className={wrapperClasses} nopadding={showCards} data-component="MajorApplianceBuyBox">
        <div className={mobileZone}>
          <div className={desktopPadding}>
            <MajorApplianceFulfillment
              itemId={itemId}
              configurableApplianceItemIds={configurableApplianceItemIds}
              covidAlert={covidAlert}
              quantity={quantity}
              onChange={onFulfillmentChange}
              bopisMinimumThreshold={bopisMinimumThreshold}
              bopisMinimumThresholdStores={bopisMinimumThresholdStores}
              isTwoTileEnabledFS={isTwoTileEnabledFS}
            />
          </div>
        </div>
        {isShowATC && (
          <div className={cardClasses}>
            <div className={`buybox__atc u--paddingLeft ${isMobile(channel) ? 'sui-m-auto' : ''}`}>
              <AddToCart
                cartOptions={helper.getCartOptions({
                  channel,
                  configurableItems: items,
                  bogoInfo,
                  fulfillmentMethod,
                  alterBrowserHistory: true,
                  zipCode,
                  storeId
                })}
                cartReqParams={helper.getCartReqParams({
                  itemId,
                  bogoInfo,
                  fulfillmentMethod,
                  configurableItems: items,
                  quantity,
                  zipCode,
                  storeId,
                  fulfillment,
                  primaryStoreNum
                })}
                onClick={onATCClick}
                disabled={disableATC}
                showIcon
              >
                {buttonText}
              </AddToCart>
            </div>
            { (showFavorites) && (
              <div className={`buybox__atc u--paddingLeft sui-mt-2 ${isMobile(channel) ? 'sui-m-auto' : ''}`}>
                <BuyboxAddToList quantity={quantity} itemId={itemId} />
              </div>
            ) }
          </div>
        )}
      </Col>
    </WrapperTag>
  );
};

MajorApplianceBuyBox.displayName = 'MajorApplianceBuyBox';
MajorApplianceBuyBox.propTypes = {
  /**
   * The number of clicks you are allowed before seeing a warning.
   */
  itemId: string,
  stash: shape({
    pageType: string,
    sharedSection: string,
    section: string,
    component: string
  }),
  showCards: bool,
  showFavorites: bool,
};

MajorApplianceBuyBox.defaultProps = {
  showFavorites: false,
  itemId: null,
  stash: {},
  showCards: false
};

export const majorApplianceDataModel = {
  clientOnlyProduct: alias('product').params({
    itemId: stringType().isRequired()
  }).shape({
    identifiers: shapeType({
      productLabel: stringType()
    }),
    fulfillment: client(params({ storeId: stringType(), zipCode: stringType() }).shape({
      fulfillmentOptions: arrayOfType(shapeType({
        type: stringType(),
        fulfillable: boolType(),
        services: arrayOfType(shapeType({
          locations: arrayOfType(shapeType({
            inventory: shapeType({
              quantity: numberType()
            }),
            isAnchor: boolType(),
            locationId: stringType(),
            storeName: stringType(),
            type: stringType()
          })),
          type: stringType()
        }))
      }))
    })),
  })
};

MajorApplianceBuyBox.dataModel = extend(
  majorApplianceDataModel,
  MajorApplianceFulfillment,
  AddToList,
);

const ErrorMABuybox = withErrorBoundary(MajorApplianceBuyBox);

export { ErrorMABuybox as MajorApplianceBuyBox };
